import React, { useState } from "react";
import { Header, Segment, Button } from "semantic-ui-react";
import { isObjectEmpty } from "../../../../utils/functions";
import { ContactCardLong } from "../../components/ContactCardLong";
import { useParams } from "react-router-dom";

import "./Contacts.scss";
import { PermitsContact } from "../../types/PermitsContact";
import { ContactInfoOptions } from "../../types/ContactInfoOptions";
import { ContactCustomField } from "../../types/ContactCustomField";
import { AddContactModal } from "../../components/AddContactModal";

// props
interface IContacts {
  contacts: PermitsContact[];
  contactCustomFields: ContactCustomField[];
  formFields: {
    [key: string]: {
      attributes: {
        contactInfoOptions: ContactInfoOptions;
        permitsExtensionDefId: number;
        visible: boolean;
      };
    };
  };
  userCanEditPermit: boolean;
}

// contacts tab on the citizen application page
export const CitizenContactsTab = ({ contacts, contactCustomFields, formFields, userCanEditPermit }: IContacts) => {
  const [customFieldToAdd, setCustomFieldToAdd] = useState<ContactCustomField | null>(null);
  const addContactModalOpen = !!customFieldToAdd;
  const { id } = useParams<{ id: string }>();

  const includeContactInfoOptions = (customField: ContactCustomField | null) => {
    if (!customField || !formFields) return null;
    const formField: any = Object.values(formFields).find(
      (formField) => formField.attributes.permitsExtensionDefId == customField.id,
    );
    return {
      ...customField,
      visibilityOptions: formField.attributes.contactInfoOptions,
    };
  };

  // returns visibility field of a form field
  const isFormFieldVisible = (customField: ContactCustomField): boolean => {
    for (const id in formFields)
      if (customField.id === formFields?.[id]?.attributes?.permitsExtensionDefId)
        return formFields[id].attributes.visible;
    return false;
  };
  const applicant = contacts.find((contact: PermitsContact) => contact.isApplicant);

  return (
    <>
      <AddContactModal
        open={addContactModalOpen}
        onClose={() => setCustomFieldToAdd(null)}
        contactCustomField={includeContactInfoOptions(customFieldToAdd)}
        customFieldType={customFieldToAdd?.type}
        permitsExtensionDefId={customFieldToAdd?.id}
        permitId={id}
        contactType={customFieldToAdd?.name}
      />
      <Segment padded className='citizen-contacts-tab public-portal-grid-segment tw-mt-0'>
        <Header as='h2'>Contacts</Header>
        {/* list of all contacts */}
        {contacts?.map((contact: PermitsContact, index: number) => {
          if (!formFields) return null;
          const customFieldType = contactCustomFields.find((customField) => {
            return customField.id == contact.permitsExtensionDefId;
          })?.type;

          if (applicant && contact.isSubmitter && contact.userId === applicant.userId) return;

          const formField = Object.values(formFields).find(
            (formField) => formField?.attributes?.permitsExtensionDefId == contact.permitsExtensionDefId,
          );
          const contactInfoOptions: ContactInfoOptions = formField?.attributes?.contactInfoOptions || {
            address: "optional",
            company_name: "optional",
            email_address: "optional",
            license_number: "optional",
            phone_number: "optional",
          };

          return (
            <ContactCardLong
              contactInfoOptions={contactInfoOptions}
              key={index}
              contact={contact}
              canAddNewContacts={customFieldType === 11} // if field type is Additional Contacts, allow editing
              userCanEditPermit={userCanEditPermit}
            />
          );
        })}

        {/* add contacts fields */}
        {contactCustomFields.map((customField, index) => {
          // if there is already information for a contact field, don't display an add button OR if form field should not be visible
          if (
            !userCanEditPermit ||
            (customField.type === 10 && !isObjectEmpty(customField.value)) ||
            !isFormFieldVisible(customField)
          )
            return;
          return (
            <Segment
              key={index}
              className='details-add-contact'
              padded
              onClick={() => setCustomFieldToAdd(customField)}
            >
              <Button
                className='no-padding-button'
                active={true}
                onClick={() => setCustomFieldToAdd(customField)}
                aria-haspopup='dialog'
              >
                Add {customField.name}
              </Button>
            </Segment>
          );
        })}
      </Segment>
    </>
  );
};
